export { useAdminer, useHasPermission, useSlot } from './chunk-MB2JPXXN.js';
import { formatPageSummary } from './chunk-FUVISKLY.js';
export { useBreakpoint } from './chunk-FUVISKLY.js';
import { useT } from './chunk-3G2YSMCN.js';
export { useT } from './chunk-3G2YSMCN.js';
import { getFormattingConfig } from './chunk-SUNYEKTQ.js';
import './chunk-WH2C42V5.js';
import './chunk-RRFUPILC.js';
import './chunk-4DPTK2CR.js';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';

function useDebouncedValue(value, delayMs = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}
function useDebouncedSearchFilter({
  value,
  onCommit,
  delayMs = 300
}) {
  const [draft, setDraftState] = useState(value);
  const timerRef = useRef(null);
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;
  useEffect(() => {
    setDraftState(value);
  }, [value]);
  useEffect(() => {
    if (draft === value) return;
    timerRef.current = setTimeout(() => {
      onCommitRef.current(draft);
    }, delayMs);
    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, [draft, value, delayMs]);
  const setDraft = useCallback((s) => {
    setDraftState(s);
  }, []);
  const flush = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setDraftState((current) => {
      onCommitRef.current(current);
      return current;
    });
  }, []);
  return { draft, setDraft, flush };
}
function useUrlFilters({
  searchParams,
  setSearchParams,
  paginationKey,
  pushToHistory = false
}) {
  const setFilters = useCallback(
    (patch) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(patch)) {
            if (value === void 0 || value === null || value === "") {
              next.delete(key);
            } else {
              next.set(key, String(value));
            }
          }
          if (!Object.prototype.hasOwnProperty.call(patch, paginationKey)) {
            next.delete(paginationKey);
          }
          return next;
        },
        { replace: !pushToHistory }
      );
    },
    [paginationKey, pushToHistory, setSearchParams]
  );
  const setFilter = useCallback(
    (key, value) => setFilters({ [key]: value }),
    [setFilters]
  );
  const updateFilters = useCallback(
    (updater) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(paginationKey);
          updater(next);
          return next;
        },
        { replace: !pushToHistory }
      );
    },
    [paginationKey, pushToHistory, setSearchParams]
  );
  return { searchParams, setFilter, setFilters, updateFilters };
}
function parseLimitParam(raw, defaultLimit) {
  return raw !== null && /^\d+$/.test(raw) && parseInt(raw, 10) > 0 ? parseInt(raw, 10) : defaultLimit;
}
function usePagination({
  nextCursor,
  searchParams,
  setSearchParams,
  defaultLimit = 50
}) {
  const cursor = searchParams.get("cursor") ?? "";
  const limit = parseLimitParam(searchParams.get("limit"), defaultLimit);
  const [cursorStack, setCursorStack] = useState([]);
  const { updateFilters } = useUrlFilters({
    searchParams,
    setSearchParams,
    paginationKey: "cursor",
    pushToHistory: true
  });
  const goNext = useCallback(() => {
    if (!nextCursor) return;
    setCursorStack((s) => [...s, cursor]);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("cursor", nextCursor);
      return next;
    });
  }, [nextCursor, cursor, setSearchParams]);
  const goPrev = useCallback(() => {
    const stack = [...cursorStack];
    const prevCursor = stack.pop() ?? "";
    setCursorStack(stack);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (prevCursor) {
        next.set("cursor", prevCursor);
      } else {
        next.delete("cursor");
      }
      return next;
    });
  }, [cursorStack, setSearchParams]);
  const resetPagination = useCallback(() => {
    setCursorStack([]);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("cursor");
      return next;
    });
  }, [setSearchParams]);
  const filterReset = useCallback(
    (updater) => {
      setCursorStack([]);
      updateFilters(updater);
    },
    [updateFilters]
  );
  const setLimit = useCallback(
    (n) => {
      setCursorStack([]);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("cursor");
        if (n !== defaultLimit) {
          next.set("limit", String(n));
        } else {
          next.delete("limit");
        }
        return next;
      });
    },
    [defaultLimit, setSearchParams]
  );
  return {
    cursor,
    limit,
    hasPrev: cursorStack.length > 0,
    hasNext: !!nextCursor,
    goNext,
    goPrev,
    setLimit,
    resetPagination,
    filterReset
  };
}
function usePaginatedQuery(useDataHook, filters, options) {
  const { searchParams, setSearchParams, defaultLimit = 50, limitOptions } = options;
  const cursor = searchParams.get("cursor") ?? "";
  const limit = parseLimitParam(searchParams.get("limit"), defaultLimit);
  const query = useDataHook({
    ...filters,
    cursor: cursor || void 0,
    limit
  });
  const pagination = usePagination({
    nextCursor: query.data?.next_cursor,
    searchParams,
    setSearchParams,
    defaultLimit
  });
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    pagination: {
      hasPrev: pagination.hasPrev,
      hasNext: pagination.hasNext,
      onPrev: pagination.goPrev,
      onNext: pagination.goNext,
      limit: pagination.limit,
      limitOptions,
      onLimitChange: pagination.setLimit
    },
    resetPagination: pagination.resetPagination,
    filterReset: pagination.filterReset
  };
}
var DEFAULT_LIMIT = 50;
function parseOffsetPageParam(raw) {
  return raw !== null && /^\d+$/.test(raw) && parseInt(raw, 10) > 0 ? parseInt(raw, 10) : 1;
}
function parseOffsetLimitParam(raw, defaultLimit, limitOptions) {
  if (!limitOptions || limitOptions.length === 0) return defaultLimit;
  const parsed = raw !== null && /^\d+$/.test(raw) && parseInt(raw, 10) > 0 ? parseInt(raw, 10) : defaultLimit;
  return limitOptions.includes(parsed) ? parsed : defaultLimit;
}
function useOffsetPaginatedQuery(useDataHook, filters, options) {
  const {
    searchParams,
    setSearchParams,
    defaultLimit = options.limitOptions?.[0] ?? DEFAULT_LIMIT,
    limitOptions,
    summaryNounForms,
    summaryFormatter
  } = options;
  const t = useT();
  const locale = getFormattingConfig().locale;
  const page = parseOffsetPageParam(searchParams.get("page"));
  const perPage = parseOffsetLimitParam(
    searchParams.get("per_page"),
    defaultLimit,
    limitOptions
  );
  const { setFilter, setFilters } = useUrlFilters({
    searchParams,
    setSearchParams,
    paginationKey: "page"
  });
  const setPage = useCallback(
    (p) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("page", String(p));
          return next;
        },
        { replace: false }
      );
    },
    [setSearchParams]
  );
  const queryFilters = useMemo(
    () => ({
      ...filters,
      page,
      per_page: perPage
    }),
    [filters, page, perPage]
  );
  const query = useDataHook(queryFilters);
  const total = query.data?.total ?? 0;
  const totalPages = total > 0 ? Math.ceil(total / perPage) : 0;
  const summaryInput = { total, page, perPage, totalPages };
  const summary = summaryFormatter ? summaryFormatter(summaryInput) : formatPageSummary(summaryInput, { nounForms: summaryNounForms, t, locale });
  useEffect(() => {
    if (!query.isLoading && !query.isError && totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages, query.isLoading, query.isError, setPage]);
  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    page,
    perPage,
    total,
    totalPages,
    setFilter,
    setFilters,
    pagination: {
      hasPrev: page > 1,
      hasNext: totalPages > 0 ? page < totalPages : false,
      onPrev: () => setPage(Math.max(1, page - 1)),
      onNext: () => setPage(page + 1),
      summary,
      prevLabel: t("pagination.prev"),
      nextLabel: t("pagination.next"),
      limitLabel: t("pagination.perPage"),
      limitAriaLabel: t("pagination.perPageAria"),
      // Only expose limit controls when the caller provides selectable options.
      ...limitOptions && limitOptions.length > 0 ? {
        limit: perPage,
        limitOptions,
        onLimitChange: (n) => setFilters({ per_page: String(n), page: void 0 })
      } : {}
    }
  };
}

export { parseLimitParam, parseOffsetLimitParam, parseOffsetPageParam, useDebouncedSearchFilter, useDebouncedValue, useOffsetPaginatedQuery, usePaginatedQuery, usePagination, useUrlFilters };
//# sourceMappingURL=hooks.js.map
//# sourceMappingURL=hooks.js.map